"use client";

import { usePathname } from "next/navigation";
import { Sidebar } from "./Sidebar";
import { Footer } from "./Footer";
import { MarketingTopbar } from "@/components/landing/MarketingTopbar";
import { TrustStrip } from "@/components/landing/TrustStrip";
import { CookieConsent } from "@/components/legal/CookieConsent";
import { classifyRoute } from "@/lib/route-classes";
import { useAuth } from "@/hooks/useAuth";

// Route-class buckets (BARE / MARKETING / APP) live in
// `lib/route-classes.ts` — single source of truth shared with
// Providers.tsx (the Authenticator wrapper). Update there if you need
// to register a new public route.
//
// F2 (plan-eng-review 2026-04-30): APP-bucket routes show a spinner
// while auth is still booting OR the user needs onboarding. Without
// this gate, a not-yet-onboarded Cognito user could see /dashboard
// chrome briefly before the onboarding probe redirects them. Backend
// gates the data, so nothing leaks — but visual flash is bad UX.
function AppLoadingSpinner() {
  return (
    <div className="flex min-h-screen flex-1 items-center justify-center">
      <div
        role="status"
        aria-label="Loading"
        className="h-8 w-8 animate-spin rounded-full border-2 border-muted-foreground/20 border-t-foreground"
      />
    </div>
  );
}

export function AppShell({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  const route = classifyRoute(pathname);
  const { isLoading, needsOnboarding } = useAuth();

  // Discovery (feed + opp-detail) uses the mockup's full-width, left-aligned
  // layout (content hugs the sidebar with page padding) rather than the
  // centered max-w-7xl column the dashboard/cases pages use.
  const wideContent = pathname.startsWith("/discovery");
  const appMainClass = wideContent
    ? "w-full flex-1 px-4 py-6 sm:px-6 lg:px-8"
    : "mx-auto w-full max-w-7xl flex-1 px-4 py-6 sm:px-6 lg:px-8";

  if (route === "bare") {
    return (
      <>
        <main className="mx-auto w-full max-w-7xl flex-1 px-4 py-6 sm:px-6 lg:px-8">
          {children}
        </main>
        <CookieConsent />
      </>
    );
  }

  if (route === "marketing") {
    const variant: "default" | "auth-login" | "auth-signup" =
      pathname === "/login" || pathname.startsWith("/login/")
        ? "auth-login"
        : pathname === "/signup" || pathname.startsWith("/signup/")
          ? "auth-signup"
          : "default";
    // The landing page (`/`) renders its own MarketingFooter, so skip
    // TrustStrip there. /login, /signup, and other marketing routes still
    // get the lean disclaimer footer.
    const isLandingHome = pathname === "/";
    // The off-white + deep-teal palette (Legora-inspired, 2026-06-05) is the
    // public *marketing* aesthetic — landing (`/`) and other content marketing
    // pages. The auth pages (/login, /signup) live in the MARKETING route
    // bucket too, but stay on the LIGHT Variant A :root tokens. Scoping
    // data-theme to variant==="default" applies the marketing palette to the
    // landing/content pages while letting auth pages fall back to :root.
    return (
      <div
        {...(variant === "default" ? { "data-theme": "marketing" } : {})}
        className="flex flex-1 flex-col bg-background text-foreground"
      >
        <MarketingTopbar variant={variant} />
        <main id="main-content" className="flex flex-1 flex-col">
          {children}
        </main>
        {!isLandingHome && <TrustStrip />}
        <CookieConsent />
      </div>
    );
  }

  // APP bucket: gate children behind auth boot + onboarding probe.
  if (isLoading || needsOnboarding) {
    return (
      <div className="flex min-h-screen flex-col md:flex-row">
        <div className="flex min-w-0 flex-1 flex-col">
          <AppLoadingSpinner />
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col md:flex-row">
      <Sidebar />
      <div className="flex min-w-0 flex-1 flex-col">
        <main id="main-content" className={appMainClass}>
          {children}
        </main>
        <Footer />
      </div>
      <CookieConsent />
    </div>
  );
}
